import { plural } from 'lib/plural';
import React from 'react';
import { IS_CLOUD_BANNED, IS_CLOUD_CREATED, IS_CLOUD_LOCKED } from 'reactApp/appHelpers/configHelpers';
import { PRAVDA_USER_POLICY_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/pravdaUserPolicy';
import { EFrom } from 'reactApp/components/SharingWindow/Sharing.types';
import { getClosestDomainBitmask } from 'reactApp/modules/domainFolders/domainFolders.selectors';
import { createNewAutoDeleteFolder, moveRequest, openPublishDialog, removeRequest } from 'reactApp/modules/modifying/modifying.actions';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { isDomainFolder as isDomainFolderStorage } from 'reactApp/modules/storage/folder.helpers';
import { isMountedOrSharedFolder, isReadOnlyMountedOrShared } from 'reactApp/modules/storage/storage.selectors';
import { store as reduxStore } from 'reactApp/store';
import { noop, noopVoid } from 'reactApp/utils/helpers';
import { getSize, getSizeInteger } from 'reactApp/utils/sizeHelper';
import { getDomainFoldersBitmaskParams } from 'server/helpers/getDomainFolderBitmaskParams';

const { renderComfirmationDialog } = process.env.SSR
    ? ({} as any)
    : require('reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers');

export const isDomainFolder = (item) => item && item.isFolder && isDomainFolderStorage(item);
export const getDomainRestrictionsForBitmask = (bitmask) => {
    const { READ_FLAG, DOWNLOAD_FLAG, EDIT_FLAG, CREATE_FLAG, SHARE_FLAG, DELETE_FLAG } = getDomainFoldersBitmaskParams(bitmask);
    return {
        isDomainAndReadRestricted: !READ_FLAG,
        isDomainAndDownloadRestricted: !DOWNLOAD_FLAG,
        isDomainAndEditRestricted: !EDIT_FLAG,
        isDomainAndCreateRestricted: !CREATE_FLAG,
        isDomainAndShareRestricted: !SHARE_FLAG,
        isDomainAndDeleteRestricted: !DELETE_FLAG,
    };
};

export const getDomainRestrictionsForItems = (items) => {
    const fullAccessBitmask = -1; /* или ~0, когда все биты установлены в 1 */
    if (!PRAVDA_USER_POLICY_ENABLED) {
        return getDomainRestrictionsForBitmask(fullAccessBitmask);
    }

    if (!Array.isArray(items)) {
        items = [items];
    }

    const reduxState = reduxStore.getState();
    const bitmask = items.reduce((result, item) => {
        const id = typeof item === 'string' ? item : item?.id;
        const bitmask = getClosestDomainBitmask(reduxState, id);
        if (bitmask !== null) {
            result = result & bitmask;
        }
        return result;
    }, fullAccessBitmask);

    return getDomainRestrictionsForBitmask(bitmask);
};

export const isReadOnly = (item) => isReadOnlyMountedOrShared(reduxStore.getState(), item);

export const isSharedOrMountedStatus = (item) => {
    const isSharedOrMounted = isMountedOrSharedFolder(reduxStore.getState(), item);
    return isSharedOrMounted && (isSharedOrMounted.isMounted || isSharedOrMounted.isShared);
};

export const getDownloadItemSize = (items, withoutRestricted = false) => {
    if (!items.length) {
        return '';
    }

    if (!items.every((item) => typeof item?.size === 'number')) {
        return;
    }

    if (items.length === 1) {
        const size =
            withoutRestricted && items[0].isFolder && 'sizeWithoutRestricted' in items[0] ? items[0].sizeWithoutRestricted : items[0].size;

        return getSize(getSizeInteger(size));
    }

    return getSize(items.reduce((acc, cur) => acc + getSizeInteger(cur.size), 0));
};

export const hideDownloadItemSize = (items) => {
    if (!items.length || !items.every((item) => typeof item?.size === 'number')) {
        return true;
    }
    return items.reduce((acc, cur) => acc + getSizeInteger(cur.size), 0) === 0;
};

export const isEmptyFolder = (currentFolder) => {
    if (!currentFolder) {
        return true;
    }

    return currentFolder.hasOwnProperty('size') && currentFolder.size === 0;
};

export const moveItemsDialog = (items, destination) => {
    if (!items || !destination) {
        return;
    }

    const { isDomainAndDeleteRestricted } = getDomainRestrictionsForItems(items);
    if (isDomainAndDeleteRestricted) {
        reduxStore.dispatch(
            showSnackbarAction({
                id: 'upload-error',
                text: 'У вас нет прав для создания, загрузки и перемещения файлов в эту папку',
                type: SnackbarTypes.failure,
                closable: true,
            })
        );
        return;
    }

    const isSharedOrMounted = isSharedOrMountedStatus(destination);
    const count = {
        folders: items.filter((item) => item.isFolder).length,
        files: 0,
    };
    count.files = items.length - count.folders;

    let text = '';
    if (items.length === 1) {
        text = `${count.folders ? 'папку' : 'файл'} «${items[0].name}»`;
    } else {
        let foldersText = '';
        if (count.folders) {
            foldersText = `${count.folders} ${['выбранную', 'выбранные', 'выбранных'][plural(count.folders)]} ${
                ['папку', 'папки', 'папок'][plural(count.folders)]
            }`;
        }
        const and = count.files && count.folders ? ' и ' : '';
        let filesText = '';
        if (count.files) {
            filesText = `${count.files} ${count.folders ? '' : ['выбранный', 'выбранных', 'выбранных'][plural(count.folders)]} ${
                ['файл', 'файла', 'файлов'][plural(count.files)]
            }`;
        }
        text = `${foldersText}${and}${filesText}`;
    }

    renderComfirmationDialog({
        dataQAId: 'move-files-confirmation-dialog',
        renderHeader: () => 'Перемещение',
        successButtonText: 'Переместить',
        renderContent: () => (
            <>
                <div style={{ overflowWrap: 'break-word' }}>{`Переместить ${text} в папку «${destination.name}»?`}</div>
                {isSharedOrMounted && (
                    <div style={{ overflowWrap: 'break-word', marginTop: '16px', fontWeight: 'normal' }} data-name="alert-text">
                        После переноса файлы будут доступны всем, у кого есть доступ к этой папке.
                    </div>
                )}
            </>
        ),
        onSuccess: () => reduxStore.dispatch(moveRequest({ items, destination: destination.id })),
        onClose: noop,
    });
};

export const removeItemsWithoutDialog = ({ items }) =>
    reduxStore.dispatch(
        removeRequest({
            items,
            withRemoveDialog: false,
        })
    );

// Удаление файлов с окном подтверждения
export const removeItems = ({ items, onSuccess = noopVoid }) => {
    reduxStore.dispatch(removeRequest({ items, withRemoveDialog: true, onSuccess }));
};

export const openShareDialog = ({ item }) =>
    reduxStore.dispatch(
        openPublishDialog({
            item,
            from: EFrom.SHARING,
        })
    );

export const createAutoDeleteFolder = () => reduxStore.dispatch(createNewAutoDeleteFolder());

export const getIsCloudAvailabe = () => IS_CLOUD_CREATED && !IS_CLOUD_BANNED && !IS_CLOUD_LOCKED;
